import { Button, Drawer, useToast } from "@helmr/ui";
import { useState } from "react";
import { isApiError } from "../../api/client";
import { convertLead, moveLead, type Lead, type Stage } from "../../api/leads";
import { useServices } from "../../app/services";
import { useInvalidateLeadData, useStaffNames, type Scope } from "./hooks";
import { leads as t } from "./strings";
import { leadName } from "./ui";
import s from "./leads.module.css";

/**
 * Lead → member (D17: one person, one record). The API links an existing member with the same phone and creates nothing,
 * so the form only asks what a new member would need; the outcome says which happened.
 */
export function ConvertDrawer({ sc, lead, stages, wonStage, open, onClose, onConverted }: {
  sc: Scope; lead: Lead; stages: Stage[]; wonStage?: Stage; open: boolean; onClose: () => void; onConverted: (r: { memberId: string; created: boolean }) => void;
}) {
  const { client } = useServices();
  const toast = useToast();
  const staff = useStaffNames(sc);
  const refresh = useInvalidateLeadData();
  const branches = sc.ctx.me?.branches ?? [];
  const [name, setName] = useState(lead.name ?? "");
  const [branchId, setBranchId] = useState(lead.branchId ?? sc.ctx.me?.branch?.id ?? branches[0]?.id ?? "");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = leadName(lead);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!lead.name && !name.trim()) { setError(t.convert.nameNeeded); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await convertLead(client, lead.id, {
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(branchId ? { homeBranchId: branchId } : {}),
        ...(owner ? { ownerStaffId: owner } : {}),
      });
      // The member now exists; a converted lead belongs in the first Won stage.
      const won = wonStage ?? stages.find((x) => x.type === "won");
      if (won && r.lead.stageId !== won.id) {
        try { await moveLead(client, lead.id, won.id); } catch { /* the conversion stands; the stage can be moved by hand */ }
      }
      await refresh();
      toast.show(r.created ? t.convert.createdMsg(name.trim() || label) : t.convert.linkedMsg(label));
      onConverted({ memberId: r.memberId, created: r.created });
      onClose();
    } catch (err) {
      setError(isApiError(err) ? err.message : t.convert.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => { if (!o && !busy) onClose(); }}
      title={t.convert.title(label)}
      description={t.convert.intro}
      footer={<>
        <Button onClick={onClose} disabled={busy}>{t.convert.cancel}</Button>
        <Button variant="primary" type="submit" form="convert-form" busy={busy} busyLabel={t.convert.converting}>{t.convert.confirm}</Button>
      </>}
    >
      <form id="convert-form" className={`${s.form} ${s.drawerPad}`} onSubmit={submit} noValidate>
        <div className={s.field}>
          <label htmlFor="cv-name">{t.convert.name}</label>
          <input id="cv-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" aria-describedby={!lead.name ? "cv-name-hint" : undefined} />
          {!lead.name ? <small id="cv-name-hint">{t.convert.nameNeeded}</small> : null}
        </div>
        {branches.length > 0 ? (
          <div className={s.field}>
            <label htmlFor="cv-branch">{t.convert.branch}</label>
            <select id="cv-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        ) : null}
        {staff.canList ? (
          <div className={s.field}>
            <label htmlFor="cv-owner">{t.convert.owner}</label>
            <select id="cv-owner" value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">{lead.ownerId ? (staff.nameOf(lead.ownerId) ?? "Current owner") : t.convert.owner}</option>
              {staff.list.filter((p) => p.id !== lead.ownerId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        ) : null}
        {error ? <p className={s.err} role="alert">{error}</p> : null}
      </form>
    </Drawer>
  );
}
